import React, { useCallback } from 'react';
import { bool, func, string } from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import Modal from '../../../components/Modal/Modal';
import { InlineTextButton, PrimaryButton } from '../../../components/Button/Button';
import { Form as FinalForm } from 'react-final-form';
import Form from '../../../components/Form/Form';
import { FORM_ERROR } from 'final-form';
import { manageDisableScrolling } from '../../../ducks/ui.duck';
import { useDispatch } from 'react-redux';
import api from '../../../api';
import FieldTextInput from '../../../components/FieldTextInput/FieldTextInput';
import css from './RequestToBuyModal.module.css';

// ako već imaš util validator, slobodno uvezi iz '../../../util/validators'
const required = msg => v => (v && v.trim() ? undefined : msg);
const maxLength = (n, msg) => v => (v && v.trim().length > n ? msg : undefined);
const combine = (...fns) => v => fns.reduce((err, fn) => err || fn(v), undefined);

const RequestToBuyModal = ({ isOpen, onClose, listingId }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );

  const onSubmit = async values => {
    const trimmed = (values.message || '').trim();
    try {
      await api.listings.requestToBuy(listingId, trimmed);
      onClose();
      alert(intl.formatMessage({ id: 'RequestToBuyModal.requestToBuySent' }));
      window.location.reload();
    } catch (err) {
      const serverMsg =
        err?.response?.data?.error || err?.message || intl.formatMessage({ id: 'General.error' });
      return { [FORM_ERROR]: serverMsg };
    }
  };

  return (
    <Modal
      id="RequestToBuyModal"
      isOpen={isOpen}
      onClose={onClose}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
      className={css.modal}
    >
      <FinalForm
        initialValues={{ message: '' }}
        onSubmit={onSubmit}
        render={({
          handleSubmit,
          submitting,
          invalid,
          pristine,
          submitError,
          dirtySinceLastSubmit,
          values,
        }) => {
          const submitDisabled = submitting || invalid || pristine;
          const submitInProgress = submitting;

          return (
            <Form onSubmit={handleSubmit}>
              <div className={css.title}>
                <FormattedMessage id="ContractLeaseActions.requestToBuy" />
              </div>

              <FieldTextInput
                id="message"
                name="message"
                type="textarea"
                className={css.field}
                label={intl.formatMessage({ id: 'RequestToBuyModal.messageLabel' })}
                placeholder={intl.formatMessage({
                  id: 'RequestToBuyModal.messagePlaceholder',
                })}
                validate={combine(
                  required(intl.formatMessage({ id: 'RequestToBuyModal.messageRequired' })),
                  maxLength(1000, intl.formatMessage({ id: 'RequestToBuyModal.messageTooLong' }))
                )}
                maxLength={1000}
                rows={6}
              />

              <small className={css.hint}>{values?.message?.length || 0}/1000</small>

              {!dirtySinceLastSubmit && submitError ? (
                <p className={css.error}>{submitError}</p>
              ) : null}

              <div className={css.btns}>
                <InlineTextButton type="button" onClick={onClose} disabled={submitting}>
                  {intl.formatMessage({ id: 'General.cancel' })}
                </InlineTextButton>

                <PrimaryButton
                  type="submit"
                  disabled={submitDisabled}
                  inProgress={submitInProgress}
                  className={css.submit}
                >
                  <FormattedMessage id="RequestToBuyModal.sendRequest" />
                </PrimaryButton>
              </div>
            </Form>
          );
        }}
      />
    </Modal>
  );
};

RequestToBuyModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  listingId: string.isRequired,
};

export default RequestToBuyModal;
